import React, { useState, useRef, useEffect } from 'react';
import { Overlay, Popover, Button } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFilePdf, faDownload, faExternalLinkAlt } from '@fortawesome/free-solid-svg-icons';
import { Link, useNavigate } from 'react-router-dom';

const DocumentPreview = ({ documentName, displayName, children, onPreview, onDownload }) => {
  const [show, setShow] = useState(false);
  const [hovering, setHovering] = useState(false);
  const targetRef = useRef(null);
  const popoverRef = useRef(null);
  const hoverTimeout = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        targetRef.current &&
        !targetRef.current.contains(event.target) &&
        popoverRef.current &&
        !popoverRef.current.contains(event.target)
      ) {
        setShow(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Handle hover logic with delay
  const handleMouseEnter = () => {
    clearTimeout(hoverTimeout.current);
    setHovering(true);
    setShow(true);
  };

  const handleMouseLeave = () => {
    hoverTimeout.current = setTimeout(() => {
      setHovering(false);
      setShow(false);
    }, 300); // Delay to avoid flicker when moving between trigger and popover
  };

  const handleDownload = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (onDownload) {
      onDownload(documentName);
    } else {
      console.log(`Initiating download for: ${documentName}`);
      const link = document.createElement('a');
      link.href = `https://select-gruppe.de/fileadmin/Downloads/Mobi/${documentName}`;
      link.download = documentName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  const handlePreviewClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (onPreview) {
      onPreview(documentName);
    }
    setShow((prev) => !prev);
  };

  const handleDocumentsClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setShow(false);
    navigate('/documents');
  };

  const popover = (
    <Popover ref={popoverRef} id={`popover-${documentName}`} style={{ maxWidth: '300px' }}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <Popover.Header as="h3" className="d-flex align-items-center">
        <FontAwesomeIcon icon={faFilePdf} className="text-danger me-2" />
        <div className="text-truncate">{displayName}</div>
      </Popover.Header>
      <Popover.Body>
        <div className="small text-muted mb-2">{documentName}</div>
        <div className="d-flex justify-content-between">
          <Button size="sm" variant="outline-primary" onClick={handleDownload}>
            <FontAwesomeIcon icon={faDownload} className="me-1" />
            Download
          </Button>
          <Button 
            size="sm" 
            variant="outline-secondary" 
            onClick={handleDocumentsClick}
          >
            <FontAwesomeIcon icon={faExternalLinkAlt} className="me-1" />
            Alle Dokumente
          </Button>
        </div>
      </Popover.Body>
    </Popover>
  );

  return (
    <>
      <span
        ref={targetRef}
        className="text-primary text-decoration-underline"
        style={{ cursor: 'pointer' }}
        onClick={handlePreviewClick}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        {children}
      </span>

      <Overlay
        target={targetRef.current}
        show={show}
        placement="top"
        containerPadding={10}
      >
        {popover}
      </Overlay>
    </>
  );
};

export default DocumentPreview;